import React, {Component} from 'react';
import '../../pages/settingsPage/SettingsPage.css';
import Page from '../../components/page/Page';
import Card from '../../components/card/Card';
import Label from '../../components/label/Label';
import CheckBox from '../../inputs/checkBox/CheckBox';
import mainAPI from '../../api/MainAPI';
import models from '../../model/models';
import toasts from '../../utils/toasts';
import debounce from 'lodash.debounce';
import Input from '../../inputs/input/Input';
import userGroupSettingsManager from '../../utils/UserGroupSettingsManager';
import SettingsTabBar from '../../components/settingsTabBar/SettingsTabBar';
import {SettingRow} from '../settingsPage/SettingsPage';

export default class GeneralSettingsPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			user_group: {},
		};
	}

	componentDidMount = async () => {
		this.subscribeId = models.user_group.onChange(this._getData);
		await this._getData();
	};

	componentWillUnmount = () => {
		if (this.subscribeId) {
			models.user_group.unsubscribe(this.subscribeId);
		}
	};

	_getData = async () => {
		let user_group = models.user_group.obj();
		if (!user_group) return;
		await userGroupSettingsManager.refresh(user_group.id);
		this.setState({user_group});
	};

	toggleUserManualScan = (value) => {
		this.toggleBoolean('allow_manual_scan_entry', value);
	};

	toggleAllowNdcBarcodeImport = (value) => {
		this.toggleBoolean('allow_ndc_barcode_import', value);
	};

	toggleRequireResealScanning = (value) => {
		this.toggleBoolean('require_reseal_scanning', value);
	};

	toggleImageUpload = (value) => {
		if (!value) {
			this.toggleRequireInventoryImage(false);
		}
		this.toggleBoolean('allow_image_upload', value);
	};

	toggleRequireInventoryImage = (value) => {
		this.toggleBoolean('require_inventory_image', value);
	};

	toggleInventoryCheckCheckbox = (value) => {
		this.toggleBoolean('allow_daily_check_with_checkbox', value);
		if (!value) this.updateSetting('enable_select_all_inventory_check_option', 0);
	};

	_saveLogoutAfterHours = async (value) => {
		await this.setPositiveNumber('auto_log_out_after_hours', value);
	};

	_updateAutoLogOutAfterHours = debounce(this._saveLogoutAfterHours, 2000);

	setPositiveNumber = async (key, value) => {
		if (value < 1) {
			return toasts.show('Value must be 1 or more');
		}
		let user_group = this.state.user_group || {};
		let userGroupWithSetting = {id: user_group.id};
		userGroupWithSetting[key] = value;
		let res = await mainAPI.user_group.update(userGroupWithSetting);
		if (res) {
			toasts.show('Setting Updated');
		} else {
			toasts.show('Setting did not update');
		}
	};

	toggleUserCreateDrug = (value) => {
		this.toggleBoolean('allow_users_to_create_drugs', value);
	};

	toggleUsageRequiresReseal = (value) => {
		this.toggleBoolean('usage_requires_reseal', value);
	};

	toggleUsageRequiresResealSupply = (value) => {
		this.toggleBoolean('usage_requires_reseal_supply', value);
	};

	toggleRequireDualFactor = (value) => {
		this.toggleBoolean('require_dual_factor', value);
	};

	toggleCanModifyIncident = (value) => {
		this.toggleBoolean('can_modify_incident_number', value);
	};

	toggleAllowReverseDistributor = (value) => {
		this.toggleBoolean('allow_reverse_distributor', value);
	};

	toggleIncidentReportMandatory = (value) => {
		this.toggleBoolean('require_incident_number', value);
	};

	toggleInventoryCheckVerifyRequired = (value) => {
		this.toggleBoolean('daily_check_verify_required', value);
	};

	toggleSelfWitness = (value) => {
		this.toggleBoolean('allow_self_witness', value);
	};

	toggleEnableSelectAllInventoryCheckOption = () => {
		const {
			user_group: {
				allow_daily_check_with_checkbox: check_box = false,
				enable_select_all_inventory_check_option: select_all = false
			} = {}
		} = this.state;
		if (!check_box) return toasts.show('Enable "Users can do inventory checks without scanning drug codes" to use this feature');
		this.toggleBoolean('enable_select_all_inventory_check_option', !select_all);
	};

	toggleBoolean = async (columnName, value) => {
		value = value ? 1 : 0;
		await this.updateSetting(columnName, value);
	};

	updateSetting = async (key, value, successMessage) => {
		let user_group = this.state.user_group || {};
		let res = await mainAPI.user_group.update({id: user_group.id, [key]: value});

		if (res) {
			user_group = this.state.user_group || {};
			user_group[key] = res[key];
			this.setState({user_group});
			toasts.show(successMessage || 'Setting Updated');
		} else {
			toasts.show('Setting did not update');
		}
	};

	render() {
		let user_group = this.state.user_group || {};
		return (
			<Page className="g-settings-page">
				<Card>
					<Label larger black>
						Account settings
					</Label>
					<div className="lower">
						<SettingsTabBar userGroup={user_group} />
						<Card className="inner-card user-settings">
							<Label bold black>Code Scanning & Entry</Label>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.allow_manual_scan_entry} onChange={this.toggleUserManualScan}/>
								<Label gray-dark>Users can manually enter a code without scanning the vial</Label>
							</SettingRow>
							<SettingRow>
								<CheckBox tabIndex="0"
									value={!!user_group.allow_ndc_barcode_import}
									onChange={this.toggleAllowNdcBarcodeImport}
								/>
								<Label gray-dark>Allow scanning the manufacture/NDC barcode to import drug details</Label>
							</SettingRow>
							<br/>
							<Label bold black>Accountability & Witnessing</Label>

							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.allow_self_witness} onChange={this.toggleSelfWitness}/>
								<Label gray-dark>Users can witness their own actions</Label>
							</SettingRow>

							<SettingRow>
								<CheckBox tabIndex="0"
									value={!!user_group.daily_check_verify_required}
									onChange={this.toggleInventoryCheckVerifyRequired}
								/>
								<Label gray-dark>Inventory checks require a witness</Label>
							</SettingRow>

							<br/>
							<Label bold black>Inventory Management</Label>

							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.allow_daily_check_with_checkbox}
									onChange={this.toggleInventoryCheckCheckbox}/>
								<Label gray-dark>Users can do inventory checks without scanning drug codes</Label>
							</SettingRow>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.enable_select_all_inventory_check_option}
									onChange={this.toggleEnableSelectAllInventoryCheckOption}/>
								<Label gray-dark>Enable "Select All" option during inventory checks</Label>
							</SettingRow>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.allow_image_upload}
									onChange={this.toggleImageUpload}/>
								<Label gray-dark>Enable uploading images during inventory checks</Label>
							</SettingRow>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.require_reseal_scanning}
									onChange={this.toggleRequireResealScanning}/>
								<Label gray-dark>Require scanning codes during a box reseal</Label>
							</SettingRow>

							<br/>
							<Label bold black>Permissions & Editing</Label>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.allow_users_to_create_drugs} onChange={this.toggleUserCreateDrug}/>
								<Label gray-dark>Users "non-admins" can add drugs</Label>
							</SettingRow>

							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.can_modify_incident_number}
									onChange={this.toggleCanModifyIncident}/>
								<Label gray-dark>Users can modify incident numbers</Label>
							</SettingRow>

							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.require_incident_number}
									onChange={this.toggleIncidentReportMandatory}/>
								<Label gray-dark>Require incident number on all administrations</Label>
							</SettingRow>

							<br/>
							<Label bold black>Drug Handling & Compliance</Label>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.allow_reverse_distributor}
									onChange={this.toggleAllowReverseDistributor}/>
								<Label gray-dark>Allow reverse distributors when wasting drugs</Label>
							</SettingRow>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.usage_requires_reseal} onChange={this.toggleUsageRequiresReseal}/>
								<Label gray-dark><b>Deployed</b> boxes must be resealed after waste, administration or box transfers</Label>
							</SettingRow>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.usage_requires_reseal_supply}
									onChange={this.toggleUsageRequiresResealSupply}/>
								<Label gray-dark><b>Supply</b> boxes must be resealed after waste, administration or box transfers</Label>
							</SettingRow>
							<br/>
							<Label bold black>Security & Session Management</Label>
							<SettingRow>
								<CheckBox tabIndex="0" value={!!user_group.require_dual_factor} onChange={this.toggleRequireDualFactor}/>
								<Label gray-dark>Require dual factor authentication</Label>
							</SettingRow>

							<SettingRow>
								<Input
									className="icon"
									value={user_group.auto_log_out_after_hours}
									onChange={this._updateAutoLogOutAfterHours}
								/>
								<Label className="auto-log-off" gray-dark>
									User will automatically be logged off after this many hours of inactivity
								</Label>
							</SettingRow>
						</Card>
					</div>
				</Card>
			</Page>
		);
	}
}




// WEBPACK FOOTER //
// ./src/pages/generalSettingsPage/GeneralSettingsPage.jsx